import {
    BudgetPhases,
    MANTY_ORDERED_BUDGET_PHASES,
    ORDERED_BUDGET_PHASES,
} from 'business/constants';
import {
    PRIMITIVE_BUDGET_AMOUNT,
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import { BUDGET_PHASES } from './phase';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';

export type ActiveBudgetPhases = Extract<
    BudgetPhases,
    | BudgetPhases.PROPOSITION_SERVICES
    | BudgetPhases.ARBITRAGE
    | BudgetPhases.VOTE
    | BudgetPhases.SUPPLEMENTARY_BUDGET
>;

export type EditableBudgetPhases = Extract<
    ActiveBudgetPhases,
    | BudgetPhases.PROPOSITION_SERVICES
    | BudgetPhases.ARBITRAGE
    | BudgetPhases.SUPPLEMENTARY_BUDGET
>;

export const getIsEditablePhase = (phase: BudgetPhases): boolean =>
    BUDGET_PHASES[phase].isEditable;

export const isActiveBudgetPhase = (phase: BudgetPhases): boolean =>
    BUDGET_PHASES[phase].isActive;

export const isSimulatedPhase = (phase: BudgetPhases): boolean =>
    BUDGET_PHASES[phase].isSimulated;

export const phaseComparator = (
    valueA: BudgetPhases,
    valueB: BudgetPhases,
): number => {
    if (valueA === valueB) {
        return 0;
    }
    if (
        ORDERED_BUDGET_PHASES.indexOf(valueA) >
        ORDERED_BUDGET_PHASES.indexOf(valueB)
    ) {
        return 1;
    }
    return -1;
};

export const getPreviousPhase = (phase: BudgetPhases): BudgetPhases | null => {
    const index = ORDERED_BUDGET_PHASES.indexOf(phase);
    return ORDERED_BUDGET_PHASES[index - 1] || null;
};

export const isLastActivePhase = (phase: BudgetPhases): boolean => {
    const index = MANTY_ORDERED_BUDGET_PHASES.indexOf(phase);
    return index === MANTY_ORDERED_BUDGET_PHASES.length - 1;
};

export const getFieldForPhase = <
    T extends keyof BudgetPhaseConstants,
    P extends BudgetPhases,
>(
    phase: P,
    field: T,
): (typeof BUDGET_PHASES)[P][T] => {
    if (!BUDGET_PHASES[phase][field]) {
        console.error(`Cannot get ${field} field for phase ${phase}`);
    }
    return BUDGET_PHASES[phase][field];
};

/** Return the Amount Field for a phase */
export const getAmountFieldForPhase = (phase: BudgetPhases) =>
    getFieldForPhase(phase, 'amount');

export const getStatusFieldForPhase = (phase: BudgetPhases) =>
    getFieldForPhase(phase, 'status');

export const getCommentForPhase = (phase: BudgetPhases) =>
    getFieldForPhase(phase, 'comment');

export type BudgetPhaseConstants =
    (typeof BUDGET_PHASES)[keyof typeof BUDGET_PHASES];

/**
 *
 * @param phase a budget phase
 * @returns the list of all the phases earlier than the given one (including the given one)
 */
export const getPhasesUpToIncludingCurrent = (phase: BudgetPhases) =>
    MANTY_ORDERED_BUDGET_PHASES.slice(
        0,
        MANTY_ORDERED_BUDGET_PHASES.indexOf(phase) + 1,
    );

export const getPhasedFieldsUntilPhase = <
    T extends keyof BudgetPhaseConstants,
    P extends BudgetPhases,
>(
    phase: P,
    field: T,
    filterPhaseType?: 'isActive' | 'isEditable' | 'isSimulated',
    filterCondition?: boolean,
) => {
    const earlierPhases = getPhasesUpToIncludingCurrent(phase);
    return earlierPhases
        .filter((phs) =>
            filterPhaseType
                ? BUDGET_PHASES[phs][filterPhaseType] === filterCondition
                : true,
        )
        .map((currentPhase) => getFieldForPhase(currentPhase, field));
};

export const isFirstActivePhase = (phase: BudgetPhases) =>
    MANTY_ORDERED_BUDGET_PHASES.indexOf(phase) === 0;

export const getEarliestActivePhase = (phases: BudgetPhases[]) => {
    const activePhases = MANTY_ORDERED_BUDGET_PHASES;
    return phases.reduce(
        (prev, curr) => {
            const currentPhaseIndex = activePhases.indexOf(curr);
            const earliestPhaseIndex = activePhases.indexOf(prev);

            const isCurrentPhaseEarlier =
                currentPhaseIndex < earliestPhaseIndex;
            if (currentPhaseIndex >= 0 && isCurrentPhaseEarlier) {
                return curr;
            }
            return prev;
        },
        activePhases[activePhases.length - 1],
    );
};

export const getMutationInformationForPhase = (
    phase: BudgetPhases | null,
    shouldDisplayError: boolean = false,
): {
    // TODO: improve this type when camelize() will have been nuked
    type: string;
    endpoint: string;
    phaseInscriptionIdColumn: keyof InscriptionT;
    inscriptionId: string;
    statusColumn: string;
} => {
    if (!phase || !isActiveBudgetPhase(phase)) {
        if (shouldDisplayError) {
            console.error(`No mutation information for phase ${phase}`);
        }
        return {
            type: '',
            endpoint: '',
            phaseInscriptionIdColumn: '' as keyof InscriptionT,
            inscriptionId: '',
            statusColumn: '',
        };
    }

    const table = getFieldForPhase(phase, 'table');
    const endpoint = getIsEditablePhase(phase)
        ? getFieldForPhase(phase, 'endpoint')
        : '';
    return {
        type: table!,
        endpoint: endpoint ?? '',
        phaseInscriptionIdColumn: 'phaseId',
        inscriptionId: table ? `${table}.inscription_id` : '',
        statusColumn: getFieldForPhase(phase, 'status')!,
    };
};

// TODO: Create a generic helper returning the phase for a given value.
export const amountMetricToPhase: Record<string, ActiveBudgetPhases> = {
    [SERVICE_PROPOSAL_AMOUNT]: BudgetPhases.PROPOSITION_SERVICES,
    [SIMULATED_ARBITRATED_AMOUNT]: BudgetPhases.ARBITRAGE,
    [PRIMITIVE_BUDGET_AMOUNT]: BudgetPhases.VOTE,
    [SUPPLEMENTARY_BUDGET_AMOUNT]: BudgetPhases.SUPPLEMENTARY_BUDGET,
};
